import { makeAutoObservable, runInAction } from "mobx";
import { Job } from "../../../types/PurchaseOrderTypes";
import { RootStore, ServiceStore } from "@intrasync/stores";

export class JobsListStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  filtersVisible = false;
  selectedJobIds: number[] = [];

  // Constructor
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  toggleFiltersVisibility() {
    this.filtersVisible = !this.filtersVisible;
  }

  toggleJobSelection(jobId: number) {
    const index = this.selectedJobIds.indexOf(jobId);
    if (index > -1) {
      this.selectedJobIds.splice(index, 1);
    } else {
      this.selectedJobIds.push(jobId);
    }
  }

  selectAllJobs() {
    this.selectedJobIds = this.jobs.map((job: any) => job.id);
  }

  clearSelection() {
    this.selectedJobIds = [];
  }

  // Async actions
  async loadJobs() {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      // Load all relational data to ensure complete information is available
      await Promise.all([
        // Load all base entities without queries to populate stores
        this.jobStore.find(),
        this.accountsStore.find(),
        this.contactsStore.find(),
        this.opportunitiesStore.find(),
        this.activitiesStore.find(),
        // Load optional stores if they exist
        this.bidsStore ? this.bidsStore.find() : Promise.resolve(),
        this.productionOrdersStore
          ? this.productionOrdersStore.find()
          : Promise.resolve(),
      ]);
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load jobs";
      });
      console.error("Failed to load jobs:", error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async reloadJobs() {
    await this.loadJobs();
  }

  async createJob(jobData: Partial<Job>) {
    this.loading = true;
    this.error = null;

    try {
      const job = await this.jobStore.create(jobData);

      // Auto-create job kickoff activity
      try {
        await this.activitiesStore.create({
          ActivityType: "Task",
          Subject: `Job kickoff: ${jobData.name}`,
          jobId: (job as Record<string, unknown>).id,
          accountId: jobData.accountId,
          Priority: "High",
          Status: "Scheduled",
          AssignedTo: this.rootStore.authStore?.currentUser?.id,
          ScheduledDate: new Date(
            Date.now() + 24 * 60 * 60 * 1000
          ).toISOString(), // Tomorrow
        });
      } catch (activityError) {
        console.warn(
          "Failed to create job kickoff activity:",
          activityError
        );
      }

      // Reload jobs to include the new one
      await this.loadJobs();

      return job;
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to create job";
      console.error("Failed to create job:", error);
      throw error;
    } finally {
      this.loading = false;
    }
  }

  async updateJob(jobId: number, jobData: Partial<Job>) {
    try {
      const updatedJob = await this.jobStore.patch(
        jobId,
        jobData
      );

      // The service store will automatically update its entities
      return updatedJob;
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to update job";
      console.error("Failed to update job:", error);
      throw error;
    }
  }

  async deleteJob(jobId: number) {
    try {
      await this.jobStore.remove(jobId);

      // Remove from selection if selected
      this.selectedJobIds = this.selectedJobIds.filter(
        (id: any) => id !== jobId
      );

      // The service store will automatically update its entities
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to delete job";
      console.error("Failed to delete job:", error);
      throw error;
    }
  }

  async deleteJobs(jobIds: number[]) {
    try {
      // Delete jobs one by one (could be optimized with bulk delete if API supports it)
      for (const jobId of jobIds) {
        await this.jobStore.remove(jobId);
      }

      // Clear selection
      this.selectedJobIds = [];

      // The service store will automatically update its entities
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to delete jobs";
      console.error("Failed to delete jobs:", error);
      throw error;
    }
  }

  async createOpportunityForJob(
    jobId: number,
    opportunityData: Record<string, unknown>
  ) {
    try {
      const job = this.jobs.find((j: any) => j.id === jobId);
      const opportunity = await this.opportunitiesStore.create({
        ...opportunityData,
        jobId: jobId,
        accountId: job?.accountId,
      });

      // Create follow-up activity
      await this.activitiesStore.create({
        ActivityType: "Follow-up",
        Subject: `Follow up on job opportunity: ${opportunityData.opportunityName}`,
        jobId: jobId,
        accountId: job?.accountId,
        opportunityId: (opportunity as Record<string, unknown>).opportunityId,
        AssignedTo: this.rootStore.authStore?.currentUser?.id,
        ScheduledDate: new Date(
          Date.now() + 3 * 24 * 60 * 60 * 1000
        ).toISOString(), // 3 days
      });

      return opportunity;
    } catch (error) {
      console.error("Failed to create opportunity for job:", error);
      throw error;
    }
  }

  // Analytics
  getJobMetrics() {
    const totalJobs = this.jobs.length;
    const totalBudget = this.jobs.reduce(
      (sum, job) => sum + (job.budget || 0),
      0
    );
    const totalActualCost = this.jobs.reduce(
      (sum, job) => sum + (job.actualCost || 0),
      0
    );
    
    const statusCounts = this.jobs.reduce((acc, job) => {
      const stage = job.stage || 'None';
      acc[stage] = (acc[stage] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const priorityCounts = this.jobs.reduce((acc, job) => {
      const priority = job.priority || 'None';
      acc[priority] = (acc[priority] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const averageBudget = totalJobs > 0 ? totalBudget / totalJobs : 0;
    const budgetVariance =
      totalBudget > 0
        ? ((totalActualCost - totalBudget) / totalBudget) * 100
        : 0;

    return {
      totalJobs,
      totalBudget,
      totalActualCost,
      averageBudget,
      budgetVariance,
      statusCounts,
      priorityCounts,
    };
  }

  // Computed values
  get jobStore(): ServiceStore {
    return this.rootStore.jobStore as ServiceStore;
  }

  get activitiesStore(): ServiceStore {
    return this.rootStore.activitiesStore as ServiceStore;
  }

  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  get accountsStore(): ServiceStore {
    return this.rootStore.accountsStore as ServiceStore;
  }

  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  get bidsStore(): ServiceStore | undefined {
    return this.rootStore.bidsStore as ServiceStore | undefined;
  }

  get productionOrdersStore(): ServiceStore | undefined {
    return this.rootStore.productionOrdersStore as ServiceStore | undefined;
  }

  get jobs(): Job[] {
    return this.jobStore.entities as unknown[] as Job[];
  }

  // Alias for backward compatibility
  get projects(): Job[] {
    return this.jobs;
  }

  get hasSelection(): boolean {
    return this.selectedJobIds.length > 0;
  }

  get isAllSelected(): boolean {
    return (
      this.selectedJobIds.length === this.jobs.length &&
      this.jobs.length > 0
    );
  }

  get hasFilters(): boolean {
    return false; // Filters are now handled by the datagrid
  }

  get availableStatuses(): string[] {
    return [...new Set(this.jobs.map((job: any) => job.stage))].filter(Boolean).sort();
  }

  get availablePriorities(): string[] {
    return [
      ...new Set(this.jobs.map((job: any) => job.priority).filter(Boolean)),
    ].sort();
  }
}

export default JobsListStore;

// Export with old name for backward compatibility
export { JobsListStore as ProjectsListStore };
